import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type CreatePensionContractInput,
  type FireProjection,
  type PensionContract,
  type PensionSettings,
  type PensionSummary,
  type UpdatePensionContractInput,
  type UpdatePensionSettingsInput,
  pensionApi,
} from "../lib/pension";

const baseKey = ["pension"] as const;

export function usePensionContracts(active?: boolean) {
  return useQuery<{ contracts: PensionContract[] }>({
    queryKey: [...baseKey, "contracts", active ?? "all"],
    queryFn: () => pensionApi.listContracts(active),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function usePensionSummary() {
  return useQuery<PensionSummary>({
    queryKey: [...baseKey, "summary"],
    queryFn: () => pensionApi.summary(),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function usePensionContract(id: string) {
  return useQuery<PensionContract>({
    queryKey: [...baseKey, "contract", id],
    queryFn: () => pensionApi.getContract(id),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
  });
}

export function useCreatePensionContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePensionContractInput) => pensionApi.createContract(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: baseKey }),
  });
}

export function useUpdatePensionContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdatePensionContractInput }) =>
      pensionApi.updateContract(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: baseKey }),
  });
}

export function useSoftDeletePensionContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pensionApi.softDeleteContract(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: baseKey }),
  });
}

export function useHardDeletePensionContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pensionApi.hardDeleteContract(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: baseKey }),
  });
}

export function usePensionSettings() {
  return useQuery<PensionSettings>({
    queryKey: [...baseKey, "settings"],
    queryFn: () => pensionApi.getSettings(),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useUpdatePensionSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePensionSettingsInput) => pensionApi.updateSettings(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: baseKey }),
  });
}

export function useFireProjection() {
  return useQuery<FireProjection>({
    queryKey: [...baseKey, "fire-projection"],
    queryFn: () => pensionApi.fireProjection(),
    staleTime: 60_000, // 1 Minute, weil von Networth/Cashflow abhaengt
  });
}
